import InterviewThreadApi from "./InterviewThreadApi";
import createClientMessageId from "./ClientMessageId";
import {TInterviewThreadApiError} from "./InterviewThreadApiError";
import {toInterviewThreadApiError} from "./InterviewThreadApiError";
import InterviewThreadStore from "./InterviewThreadStore";
import ProcessingJobMonitor from "./ProcessingJobMonitor";
import {
    isProcessingJobStatus,
    TInterviewMessage,
    TInterviewProcessingJob,
} from "./TInterviewThread";

type TInterviewThreadControllerEffects = {
    onChange: () => void,
    onMessagesChanged: () => void,
    onWaitingForUser: () => void,
    onNewAgentMessages: (
        messages: TInterviewMessage[],
        previousMessageIds: Set<number>,
    ) => void,
};

export default class InterviewThreadController {

    private loadController: AbortController|null = null;
    private sendController: AbortController|null = null;
    private requeueController: AbortController|null = null;
    private destroyed = false;
    private requeueDeadOnNextStatus = false;

    constructor(
        private readonly api: InterviewThreadApi,
        private readonly store: InterviewThreadStore,
        private readonly processingJobMonitor: ProcessingJobMonitor,
        private readonly effects: TInterviewThreadControllerEffects,
        private readonly translate: (key: string) => string,
        private readonly interviewId: number,
        private readonly testMode: boolean,
        private readonly clientMessageIdFactory: () => string = createClientMessageId,
    ) {
    }

    public async init(): Promise<void> {
        await this.loadThread(true, true);
    }

    public onInput(input: string): void {
        this.store.dispatch({type: "inputChanged", input});
        this.effects.onChange();
    }

    public async send(): Promise<void> {
        if (!this.store.canSend()) {
            return;
        }

        await this.submitMessage(this.clientMessageIdFactory());
    }

    private async submitMessage(clientMessageId: string): Promise<void> {

        const content = this.store.getState().input.trim();
        this.store.dispatch({type: "submitStarted"});
        this.effects.onChange();
        this.sendController?.abort();
        const requestController = new AbortController();
        this.sendController = requestController;

        try {
            const response = await this.api.sendMessage(
                this.interviewId,
                content,
                this.testMode,
                clientMessageId,
                requestController.signal,
            );
            if (this.destroyed || requestController.signal.aborted) {
                return;
            }
            if (!response.success) {
                this.store.dispatch({
                    type: "submitRejected",
                    error: this.error("validation", response.error, false),
                });
                return;
            }

            this.store.dispatch({
                type: "submitSucceeded",
                message: response.message,
                jobId: response.processingJobId,
            });
            this.effects.onMessagesChanged();
            this.startMonitoring(response.processingJobId);
        } catch (error) {
            if (requestController.signal.aborted || this.destroyed) {
                return;
            }

            const apiError = this.normalizeApiError(
                error,
                this.translate("interview_thread_message_processing_failed"),
            );
            if (apiError.kind === "network"
                || apiError.kind === "server"
                || apiError.kind === "timeout"
            ) {
                this.store.dispatch({type: "sendFailed", error: apiError, clientMessageId});
            } else {
                this.store.dispatch({type: "submitRejected", error: apiError});
            }
        } finally {
            if (this.sendController === requestController) {
                this.sendController = null;
            }
            if (!this.destroyed) {
                this.effects.onChange();
            }
        }
    }

    public async retry(): Promise<void> {
        const conversation = this.store.getState().conversation;
        if ((conversation.type === "sendFailed"
            || conversation.type === "loadFailed"
            || conversation.type === "processingFailed"
            || conversation.type === "reconciliationRequired")
            && !conversation.error.retryable
        ) {
            return;
        }

        if (conversation.type === "sendFailed") {
            await this.submitMessage(conversation.clientMessageId);
            return;
        }
        if (conversation.type === "loadFailed"
            || conversation.type === "reconciliationRequired"
        ) {
            await this.loadThread(true, false);
            return;
        }

        if (conversation.type !== "processingFailed") {
            return;
        }

        if (conversation.retryMode === "requeue") {
            await this.requeueProcessingJob(conversation.jobId);
            return;
        }

        this.requeueDeadOnNextStatus = conversation.retryMode === "verify";
        this.store.dispatch({
            type: "processingStatus",
            jobId: conversation.jobId,
            failedSince: null,
        });
        this.effects.onChange();
        this.startMonitoring(conversation.jobId, true);
    }

    public destroy(): void {
        this.destroyed = true;
        this.processingJobMonitor.stop();
        this.loadController?.abort();
        this.sendController?.abort();
        this.requeueController?.abort();
        this.loadController = null;
        this.sendController = null;
        this.requeueController = null;
    }

    private async loadThread(showLoading: boolean, initial: boolean): Promise<void> {
        this.loadController?.abort();
        this.processingJobMonitor.stop();
        const wasReconciling = this.store.getState().conversation.type === "reconciliationRequired";
        if (showLoading) {
            this.store.dispatch({type: "loadStarted"});
            this.effects.onChange();
        }

        const previousMessages = this.store.getState().messages;
        const previousMessageIds = new Set(previousMessages.map(message => message.id));
        const requestController = new AbortController();
        this.loadController = requestController;

        try {
            const thread = await this.api.getThread(requestController.signal);
            if (this.destroyed || requestController.signal.aborted) {
                return;
            }

            this.store.dispatch({
                type: "threadLoaded",
                thread,
                missingJobError: this.error(
                    "validation",
                    this.translate("interview_thread_active_job_missing"),
                    true,
                ),
            });
            if (wasReconciling && thread.messages.some(message => {
                return message.role === "user" && !previousMessageIds.has(message.id);
            })) {
                this.store.dispatch({type: "inputChanged", input: ""});
            }
            if (thread.messages.length > previousMessages.length) {
                this.effects.onMessagesChanged();
            }
            if (!initial) {
                this.effects.onNewAgentMessages(thread.messages, previousMessageIds);
            }

            if (thread.status === "waiting_for_user") {
                this.effects.onWaitingForUser();
            } else if (thread.status === "waiting_for_agent" && thread.activeProcessingJob !== null) {
                this.handleInitialProcessingJob(thread.activeProcessingJob);
            }
        } catch (error) {
            if (requestController.signal.aborted || this.destroyed) {
                return;
            }

            this.store.dispatch({
                type: "loadFailed",
                error: this.normalizeApiError(
                    error,
                    this.translate("interview_thread_load_error"),
                ),
            });
        } finally {
            if (this.loadController === requestController) {
                this.loadController = null;
            }
            if (!this.destroyed) {
                this.effects.onChange();
            }
        }
    }

    private handleInitialProcessingJob(job: TInterviewProcessingJob): void {
        const status = job.status.toLowerCase();
        if (status === "dead") {
            this.failProcessing(
                job.id,
                this.error(
                    "server",
                    this.translate("interview_thread_message_processing_failed"),
                    true,
                ),
                "requeue",
            );
            return;
        }
        if (!isProcessingJobStatus(status)) {
            this.failProcessing(
                job.id,
                this.error(
                    "validation",
                    this.translate("interview_thread_unknown_job_status"),
                    true,
                ),
                "resume",
            );
            return;
        }

        this.startMonitoring(job.id, true);
    }

    private startMonitoring(jobId: number, immediate = false): void {
        this.processingJobMonitor.start(jobId, {
            onStatus: async (job, failedSince) => this.handleJobStatus(job, failedSince),
            onFailedTimeout: timedOutJobId => {
                this.failProcessing(
                    timedOutJobId,
                    this.error(
                        "timeout",
                        this.translate("interview_thread_processing_timeout"),
                        true,
                    ),
                    "verify",
                );
            },
            onError: (error, failedJobId) => {
                this.failProcessing(
                    failedJobId,
                    this.normalizeApiError(
                        error,
                        this.translate("interview_thread_message_processing_failed"),
                    ),
                    "resume",
                );
            },
        }, immediate);
    }

    private async handleJobStatus(
        job: TInterviewProcessingJob,
        failedSince: number|null,
    ): Promise<void> {
        if (this.destroyed) {
            return;
        }

        const status = job.status.toLowerCase();
        if (!isProcessingJobStatus(status)) {
            this.processingJobMonitor.stop();
            this.failProcessing(
                job.id,
                this.error(
                    "validation",
                    this.translate("interview_thread_unknown_job_status"),
                    true,
                ),
                "resume",
            );
            return;
        }

        if (status === "done") {
            this.processingJobMonitor.stop();
            await this.loadThread(false, false);
            return;
        }

        if (status === "dead") {
            this.processingJobMonitor.stop();
            if (this.requeueDeadOnNextStatus) {
                this.requeueDeadOnNextStatus = false;
                await this.requeueProcessingJob(job.id);
                return;
            }
            this.failProcessing(
                job.id,
                this.error(
                    "server",
                    this.translate("interview_thread_message_processing_failed"),
                    true,
                ),
                "requeue",
            );
            return;
        }

        this.requeueDeadOnNextStatus = false;
        this.store.dispatch({
            type: "processingStatus",
            jobId: job.id,
            failedSince,
        });
        this.effects.onChange();
    }

    private async requeueProcessingJob(jobId: number): Promise<void> {
        this.requeueController?.abort();
        const requestController = new AbortController();
        this.requeueController = requestController;
        this.store.dispatch({type: "processingStatus", jobId, failedSince: null});
        this.effects.onChange();

        try {
            const response = await this.api.requeueProcessingJob(jobId, requestController.signal);
            if (this.destroyed || requestController.signal.aborted) {
                return;
            }
            this.store.dispatch({
                type: "processingStatus",
                jobId: response.job.id,
                failedSince: null,
            });
            this.startMonitoring(response.job.id, true);
        } catch (error) {
            if (requestController.signal.aborted || this.destroyed) {
                return;
            }
            this.failProcessing(
                jobId,
                this.normalizeApiError(
                    error,
                    this.translate("interview_thread_message_processing_failed"),
                ),
                "requeue",
            );
        } finally {
            if (this.requeueController === requestController) {
                this.requeueController = null;
            }
            if (!this.destroyed) {
                this.effects.onChange();
            }
        }
    }

    private failProcessing(
        jobId: number,
        error: TInterviewThreadApiError,
        retryMode: "resume"|"verify"|"requeue",
    ): void {
        this.store.dispatch({
            type: "processingFailed",
            jobId,
            error,
            retryMode,
        });
        this.effects.onChange();
    }

    private error(
        kind: TInterviewThreadApiError["kind"],
        message: string,
        retryable: boolean,
    ): TInterviewThreadApiError {
        return {kind, message, retryable};
    }

    private normalizeApiError(error: unknown, fallbackMessage: string): TInterviewThreadApiError {
        const apiError = toInterviewThreadApiError(error, fallbackMessage);
        if (apiError.kind !== "network") {
            return apiError;
        }

        return {
            ...apiError,
            message: this.translate("interview_thread_network_error"),
        };
    }

}
